import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import Select from "react-select";

import { useToast } from "src/context/ToastContext";
import { CriteriaGroupEntity } from "src/entities/CriteriaGroupEntity";
import { CriteriaClassificationEntity } from "src/entities/CriteriaClassificationEntity";
import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import CriteriaGroupService, { criteriaGroupQueryKeys } from "src/services/CriteriaGroupService";
import CriteriaClassificationService, { criteriaClassificationQueryKeys } from "src/services/CriteriaClassificationService";
import CriteriaManifestationService, { criteriaManifestationQueryKeys } from "src/services/CriteriaManifestationService";
import { SelectOption, SelectOptions, SelectStyle } from "src/config/select";
import Navigation from "./Navigation";
import { clearCriteriaStepDraft, getCriteriaStepDraft, setCriteriaStepDraft } from "./draftStorage";

const criteriaGroupService = new CriteriaGroupService();
const criteriaClassificationService = new CriteriaClassificationService();
const criteriaManifestationService = new CriteriaManifestationService();

type Manifestation = {
	id?: number;
	title: string;
	groups: number[];
	classifications: number[];
	technicalImportance: number | null;
	functionalImportance: number | null;
	originalTitle?: string;
	originalClassifications?: number[];
	originalGroups?: number[];
	originalTechnicalImportance?: number | null;
	originalFunctionalImportance?: number | null;
};

type StepManifestationsDraft = {
	manifestations: Manifestation[];
	removedIds: number[];
};

type StepManifestationsProps = {
	criteriaId: number;
	backLabel: string;
	nextLabel: string;
	onBack: () => void;
	onComplete: () => void;
};

export default function StepManifestations({ criteriaId, backLabel, nextLabel, onBack, onComplete }: StepManifestationsProps) {
	const { handleToast } = useToast();
	const draftRef = useRef(getCriteriaStepDraft<StepManifestationsDraft>(criteriaId, "manifestations"));
	const [manifestations, setManifestations] = useState<Manifestation[]>(() => draftRef.current?.manifestations ?? []);
	const [removedIds, setRemovedIds] = useState<number[]>(() => draftRef.current?.removedIds ?? []);

	const criteriaIdStr = String(criteriaId);

	const { data: groupsData, isLoading: groupsLoading } = useQuery<CriteriaGroupEntity[]>(
		criteriaGroupQueryKeys.groups(criteriaIdStr),
		() => criteriaGroupService.getGroups(criteriaId).then(response => response.data.groups),
		{ refetchOnWindowFocus: false }
	);

	const { data: classificationsData, isLoading: classificationsLoading } = useQuery<CriteriaClassificationEntity[]>(
		criteriaClassificationQueryKeys.classifications(criteriaIdStr),
		() => criteriaClassificationService.getClassifications(criteriaId).then(response => response.data.classifications),
		{ refetchOnWindowFocus: false }
	);

	const { data: manifestationsData, isLoading: manifestationsLoading } = useQuery<CriteriaManifestationEntity[]>(
		criteriaManifestationQueryKeys.manifestations(criteriaIdStr),
		() => criteriaManifestationService.getManifestations(criteriaId).then(response => response.data.manifestations),
		{ refetchOnWindowFocus: false }
	);

	useEffect(() => {
		if (!draftRef.current && manifestationsData) {
			setManifestations(manifestationsData.map(manifestation => {
				const classificationIds = manifestation.classifications.map(classification => classification.id);
				const groupIds = manifestation.groups.map(group => group.id);

				return {
					id: manifestation.id,
					title: manifestation.title,
					groups: groupIds,
					classifications: classificationIds,
					technicalImportance: manifestation.technicalImportance,
					functionalImportance: manifestation.functionalImportance,
					originalTitle: manifestation.title,
					originalClassifications: classificationIds,
					originalGroups: groupIds,
					originalTechnicalImportance: manifestation.technicalImportance,
					originalFunctionalImportance: manifestation.functionalImportance,
				};
			}));
		}
	}, [manifestationsData]);

	const groupOptions = groupsData?.map(group => ({ label: group.title, value: group.id })) ?? [];
	const classificationOptions = classificationsData?.map(classification => ({ label: classification.title, value: classification.id })) ?? [];
	const isLoading = groupsLoading || classificationsLoading || manifestationsLoading;

	async function saveCreated(created: Manifestation[]) {
		if (created.length > 0) {
			await criteriaManifestationService.postManifestations({
				criteriaId,
				manifestations: created.map(manifestation => ({
					title: manifestation.title,
					groups: manifestation.groups,
					classifications: manifestation.classifications,
					technicalImportance: manifestation.technicalImportance,
					functionalImportance: manifestation.functionalImportance,
				})),
			});
		}
	}

	async function saveUpdated(updated: Manifestation[]) {
		if (updated.length > 0) {
			await criteriaManifestationService.putManifestations({
				manifestations: updated.map(manifestation => ({
					id: manifestation.id!,
					title: manifestation.title,
					technicalImportance: manifestation.technicalImportance,
					functionalImportance: manifestation.functionalImportance,
				})),
			});
		}
	}

	async function saveDeleted() {
		if (removedIds.length > 0) {
			await criteriaManifestationService.deleteManifestations({ criteriaId, manifestations: removedIds });
		}
	}

	async function syncClassifications(existing: Manifestation[]) {
		const calls: Promise<unknown>[] = [];

		for (const manifestation of existing) {
			const original = manifestation.originalClassifications ?? [];
			const added = manifestation.classifications.filter(id => !original.includes(id));
			const removed = original.filter(id => !manifestation.classifications.includes(id));

			if (added.length > 0) {
				calls.push(criteriaManifestationService.postManifestationClassifications({ manifestationId: manifestation.id!, classifications: added }));
			}

			for (const classificationId of removed) {
				calls.push(criteriaManifestationService.deleteManifestationClassification(manifestation.id!, classificationId));
			}
		}

		await Promise.all(calls);
	}

	async function syncGroups(existing: Manifestation[]) {
		const calls: Promise<unknown>[] = [];

		for (const manifestation of existing) {
			const original = manifestation.originalGroups ?? [];
			const added = manifestation.groups.filter(id => !original.includes(id));
			const removed = original.filter(id => !manifestation.groups.includes(id));

			if (added.length > 0) {
				calls.push(criteriaManifestationService.postManifestationGroups({ manifestationId: manifestation.id!, groups: added }));
			}

			for (const groupId of removed) {
				calls.push(criteriaManifestationService.deleteManifestationGroup(manifestation.id!, groupId));
			}
		}

		await Promise.all(calls);
	}

	const mutation = useMutation(
		async () => {
			const created = manifestations.filter(manifestation => !manifestation.id && manifestation.title.trim());
			const updated = manifestations.filter(manifestation => manifestation.id && (
				manifestation.title !== manifestation.originalTitle ||
				manifestation.technicalImportance !== manifestation.originalTechnicalImportance ||
				manifestation.functionalImportance !== manifestation.originalFunctionalImportance
			));
			const existing = manifestations.filter(manifestation => manifestation.id);

			await Promise.all([
				saveCreated(created),
				saveUpdated(updated),
				saveDeleted(),
				syncClassifications(existing),
				syncGroups(existing),
			]);
		},
		{
			onSuccess: () => {
				clearCriteriaStepDraft(criteriaId, "manifestations");
				onComplete();
			},
			onError: (err: AxiosError) => {
				handleToast("Critério", `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleAdd() {
		setManifestations(prev => [...prev, { title: "", groups: [], classifications: [], technicalImportance: null, functionalImportance: null }]);
	}

	function handleNext() {
		if (manifestations.length === 0) {
			handleToast("Critério", "É necessário adicionar pelo menos uma manifestação para continuar.", 5000, "danger");
			return;
		}

		const hasManifestationWithoutGroup = manifestations.some(manifestation => manifestation.groups.length === 0);
		const hasManifestationWithoutClassification = manifestations.some(manifestation => manifestation.classifications.length === 0);

		if (hasManifestationWithoutGroup) {
			handleToast("Critério", "Todas as manifestações precisam estar vinculadas a pelo menos um grupo.", 5000, "danger");
			return;
		}

		if (hasManifestationWithoutClassification) {
			handleToast("Critério", "Todas as manifestações precisam estar vinculadas a pelo menos uma classificação.", 5000, "danger");
			return;
		}

		mutation.mutate();
	}

	function handleTitleChange(index: number, value: string) {
		setManifestations(prev => prev.map((manifestation, idx) => idx === index ? { ...manifestation, title: value } : manifestation));
	}

	function handleTechnicalImportanceChange(index: number, value: string) {
		setManifestations(prev => prev.map((manifestation, idx) => idx === index ? { ...manifestation, technicalImportance: value === "" ? null : Number(value) } : manifestation));
	}

	function handleFunctionalImportanceChange(index: number, value: string) {
		setManifestations(prev => prev.map((manifestation, idx) => idx === index ? { ...manifestation, functionalImportance: value === "" ? null : Number(value) } : manifestation));
	}

	function handleGroupsChange(index: number, value: readonly SelectOption[] | null) {
		setManifestations(prev => prev.map((manifestation, idx) => idx === index ? { ...manifestation, groups: value ? value.map(option => option.value) : [] } : manifestation));
	}

	function handleClassificationsChange(index: number, value: readonly SelectOption[] | null) {
		setManifestations(prev => prev.map((manifestation, idx) => idx === index ? { ...manifestation, classifications: value ? value.map(option => option.value) : [] } : manifestation));
	}

	function handleRemove(index: number) {
		const item = manifestations[index];
		
		if (item.id) {
			setRemovedIds(prev => [...prev, item.id!]);
		}

		setManifestations(prev => prev.filter((_, idx) => idx !== index));
	}

	function handleBack() {
		setCriteriaStepDraft(criteriaId, "manifestations", { manifestations, removedIds });
		onBack();
	}

	return (
		<>
			<Card className="mb-4">
				<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
					Manifestações
				</Card.Header>
				<Card.Body>
					{isLoading ? (
					Array.from({ length: 3 }, (_, skeletonIndex) => (
							<div key={skeletonIndex} className="mb-3">
								<Row className="mb-2 gy-2">
									<Col md={12} lg={4}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col md={6} lg={4}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col md={6} lg={4}>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
								</Row>
								<Row className="align-items-end gy-2">
									<Col xs={12} lg>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col xs={12} lg>
										<div className="skeleton skeleton-text skeleton-text-title mb-1" style={{ height: 16 }} />
										<div className="skeleton skeleton-button" style={{ height: 38 }} />
									</Col>
									<Col xs={12} lg="auto" className="d-flex align-items-end">
										<div className="skeleton skeleton-button" style={{ height: 38, width: 44 }} />
									</Col>
								</Row>
							</div>
						))
					) : manifestations.length === 0 ? (
						<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
							<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
							Nenhum registro adicionado.	
						</div>
					) : (
						manifestations.map((manifestation, index) => (
							<div
								key={index}
								className={index < manifestations.length - 1 ? "border-bottom pb-4 mb-3" : "mb-3"}
							>
								<Row className="mb-2 gy-2">
									<Col md={12} lg={4}>
										<Form.Label className="text-muted"><small>Título *</small></Form.Label>
										<Form.Control
											type="text"
											placeholder="Informe aqui"
											required
											value={manifestation.title}
											onChange={(e) => handleTitleChange(index, e.target.value)}
										/>
									</Col>
									<Col md={6} lg={4}>
										<Form.Label className="text-muted"><small>Importância Técnica *</small></Form.Label>
										<Form.Control
											type="number"
											step="any"
											placeholder="Informe aqui"
											required
											value={manifestation.technicalImportance ?? ""}
											onChange={(e) => handleTechnicalImportanceChange(index, e.target.value)}
										/>
									</Col>
									<Col md={6} lg={4}>
										<Form.Label className="text-muted"><small>Importância Funcional *</small></Form.Label>
										<Form.Control
											type="number"
											step="any"
											placeholder="Informe aqui"
											required
											value={manifestation.functionalImportance ?? ""}
											onChange={(e) => handleFunctionalImportanceChange(index, e.target.value)}
										/>
									</Col>
								</Row>
								<Row className="align-items-end gy-2">
									<Col xs={12} lg>
										<Form.Label className="text-muted"><small>Classificações *</small></Form.Label>
										<Select
											placeholder="Selecione"
											isMulti
											options={classificationOptions}
											value={classificationOptions.filter(opt => manifestation.classifications.includes(opt.value))}
											onChange={(value) => handleClassificationsChange(index, value)}
											styles={SelectStyle}
											{...SelectOptions}
										/>
									</Col>
									<Col xs={12} lg>
										<Form.Label className="text-muted"><small>Grupos *</small></Form.Label>
										<Select
											placeholder="Selecione"
											isMulti
											options={groupOptions}
											value={groupOptions.filter(opt => manifestation.groups.includes(opt.value))}
											onChange={(value) => handleGroupsChange(index, value)}
											styles={SelectStyle}
											{...SelectOptions}
										/>
									</Col>
									<Col xs={12} lg="auto" className="d-flex align-items-end">
										<Button variant="light" className="rounded-3 shadow-sm" type="button" onClick={() => handleRemove(index)}>
											<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
										</Button>
									</Col>
								</Row>
							</div>
						))
					)}
				</Card.Body>
				<Card.Footer className="bg-white">
					<Button
						variant="light"
						className="d-flex rounded-3 shadow-sm"
						type="button"
						onClick={handleAdd}
						disabled={isLoading}
					>
						<Icon path={mdiPlus} size={1} /> Adicionar Manifestação
					</Button>
				</Card.Footer>
			</Card>
			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={mutation.isLoading}
				onBack={handleBack}
				onNext={handleNext}
			/>
		</>
	);
}
